'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();

import { analytics } from '@/lib/analytics/posthog'
import { useEffect, useState } from 'react'

import type EchoType from 'laravel-echo'

import { FormErrorBanner } from '@/components/site/forms/form-error-banner'
import { Button } from '@/components/ui/button'
import { pusherAppCluster, pusherAppKey } from '@/config/env.public'
import { firePixelConversion } from '@/lib/analytics/pixels'
import { RegisterProgressBar } from './register-progress-bar'
import type { RegisterCopy, RegisterData } from './register-types'

type CreatingStepProps = {
  copy: RegisterCopy['creating']
  data: RegisterData
  onRestart: (data: RegisterData) => void
}

export function RegisterCreatingStep({ copy, data, onRestart }: CreatingStepProps) {
  const hasId = Boolean(data.id)
  const [loading, setLoading] = useState(hasId)
  const [url, setUrl] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(hasId ? null : copy.errorMessage)

  useEffect(() => {
    if (!data.id) {
      return
    }

    // laravel-echo + pusher-js are only needed once a visitor reaches this final step; a static
    // import would put the whole websocket stack in the signup page's initial bundle.
    let echo: EchoType<'pusher'> | null = null
    let cancelled = false

    const connect = async () => {
      const [{ default: Echo }, { default: Pusher }] = await Promise.all([import('laravel-echo'), import('pusher-js')])
      if (cancelled) {
        return
      }

      echo = new Echo({
        broadcaster: 'pusher',
        Pusher,
        key: pusherAppKey,
        cluster: pusherAppCluster,
        forceTLS: true,
        // `channelAuthorization` rather than `authEndpoint`, because the
        // endpoint now needs a parameter alongside it and pusher-js only
        // carries extra params on this shape (`auth: { params }` is its
        // deprecated spelling of the same thing).
        //
        // The hash is proof that this browser is the one that registered.
        // Client ids are sequential, so without it the Manager could only ask
        // "is this client new?" — and every fresh registration was listenable
        // by anyone willing to count. `hash` came back from POST /register and
        // reaches nobody else.
        channelAuthorization: {
          endpoint: `/api/register/broadcasting/auth`,
          transport: 'ajax',
          params: { hash: data.hash ?? '' },
        },
      })

      const channel = echo.private(`client.${data.id}`)

      channel.listen('.creation.success', (payload: { url?: string }) => {
        // Shared dedup id: the backend's `start_trial.<hash>` eventId, also used by the server-side
        // registration event and the backend FacebookStartTrial CAPI, so everything counts once.
        const eventId = data.eventId

        // No `signup_completed` here: the manager emits it from the code that actually provisions
        // the workspace. The two do not deduplicate inside PostHog — it dedupes on
        // (timestamp, distinct_id, event, uuid) and the two halves send different uuids — so both
        // being present stored every signup twice. The manager's half cannot be ad-blocked or lost
        // to a closed tab, and it fires on the workspace existing rather than on a message about it.
        // The funnel still stitches: `identifyByEmail()` at the email step uses the same normalised
        // address the manager uses as its distinct_id.

        // Fire the browser ad pixels (StartTrial), deduped against the server-side CAPI by eventId.
        firePixelConversion('signup', {
          eventId,
          email: data.email,
          phone: data.phone,
          firstName: data.firstName,
          lastName: data.lastName,
          // The person, from the backend, so the pixel and the server-side CAPI
          // name the same one. This was `data.hash` — the *client* — which meant
          // every seat of an office claimed to be the same human, and stopped
          // matching the server entirely once that moved to the lead's uuid.
          externalId: data.externalId,
        })

        setLoading(false)
        if (payload?.url) {
          setUrl(payload.url)
        }
      })

      channel.listen('.creation.failed', (payload: { error?: string }) => {
        // The reason travels with the event. Without it this says only that
        // something went wrong for somebody, which cannot be counted, grouped or
        // acted on — and provisioning is the one step of this funnel where the
        // visitor has already done everything right.
        analytics.capture('signup_failed', {
          reason: payload?.error ?? 'unknown',
          conversion_id: data.eventId,
        })

        setLoading(false)
        setErrorMessage(payload?.error || copy.errorMessage)
      })
    }

    void connect()

    return () => {
      cancelled = true
      echo?.leave(`private-client.${data.id}`)
      echo?.disconnect()
    }
  }, [copy.errorMessage, data])

  return (
    <div className="space-y-8">
      {errorMessage && (
        <div className="space-y-4">
          <FormErrorBanner className="font-medium">{errorMessage}</FormErrorBanner>
          <Button type="button" size="lg" color="dark/zinc" onClick={() => onRestart(data)}>
            {copy.restartLabel}
          </Button>
        </div>
      )}

      {loading && (
        <div className="space-y-6 py-8 text-center">
          <p className="text-sm font-semibold tracking-wider text-mist-400 uppercase dark:text-white/70">
            {copy.loadingEyebrow}
          </p>
          <h2 className="text-2xl font-semibold text-mist-950 dark:text-white">{copy.loadingTitle}</h2>
          <p className="text-sm text-mist-500 dark:text-white/70">{copy.loadingBody}</p>
          <RegisterProgressBar seconds={9} />
        </div>
      )}

      {!loading && !errorMessage && (
        <div className="space-y-6 py-6 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
            <CheckIcon />
          </div>
          <div>
            <p className="text-sm font-semibold tracking-wider text-mist-400 uppercase dark:text-white/70">
              {copy.successEyebrow}
            </p>
            <h2 className="mt-2 text-2xl font-semibold text-mist-950 dark:text-white">{copy.successTitle}</h2>
            <p className="mt-3 text-sm text-mist-500 dark:text-white/70">{copy.successBody}</p>
          </div>
          {url ? (
            <Button href={url} color="dark/zinc" size="lg">
              {copy.successStartLabel}
            </Button>
          ) : (
            <Button type="button" size="lg" plain>
              {copy.successEmailLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true" fill="none" stroke="currentColor">
      <path d="M5 13l4 4L19 7" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
